import React, { useCallback, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import { useCancelStringGroupTranslationMutation } from 'src/generated/graphql';

interface CancelTranslationModalProps {
  visible: boolean;
  namespaceId: string;
  stringGroupId: string;
  version: number;
  onDismiss: () => void;
  onSuccess: () => void;
}

const CancelTranslationModal: React.FC<CancelTranslationModalProps> = ({
  visible,
  namespaceId,
  stringGroupId,
  version,
  onDismiss,
  onSuccess,
}) => {
  // The reason is captured for the operator's own record only. It is optional
  // because cancelStringGroupTranslation takes no reason argument, so requiring
  // it would force users to type a value that is never sent anywhere. Make it
  // mandatory again once the schema accepts a reason.
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [cancelMutation, { loading }] =
    useCancelStringGroupTranslationMutation();

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      await cancelMutation({
        variables: { namespaceId, stringGroupId, version: version.toString() },
      });
      setReason('');
      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to cancel translation. Please try again.',
      );
    }
  }, [namespaceId, stringGroupId, version, cancelMutation, onSuccess]);

  const handleDismiss = useCallback(() => {
    setReason('');
    setError(null);
    onDismiss();
  }, [onDismiss]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Cancel Translation"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={loading}>
              Go Back
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={loading}
              className="btn-danger"
            >
              <Box color="text-status-error" fontWeight="bold">
                Confirm Cancellation
              </Box>
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">
          <strong>Warning:</strong> The entire string group translation will be
          cancelled. Individual strings cannot be cancelled separately.
        </Alert>

        {error && <Alert type="error">{error}</Alert>}

        <FormField
          label="Reason for cancellation"
          constraintText="Optional — recorded for your reference only."
        >
          <Textarea
            value={reason}
            onChange={({ detail }) => setReason(detail.value)}
            placeholder="Optionally note why this translation job is being cancelled..."
            rows={3}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default CancelTranslationModal;
